import { bookedWeeksFromDays, roundBookedWeeks } from '@sea-snag/shared';
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sea-snag/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useCopyRevenueGoals,
  useDeleteRevenueGoal,
  useRevenueGoals,
  useUpsertRevenueGoal,
} from '../../hooks/use-revenue-goals';
import type { BoatRevenueBreakdown } from '../../lib/api-client';
import { analyticsApi } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import {
  boatsToCopyGoals,
  buildGoalDrafts,
  type GoalDraft,
  type GoalStatus,
  goalStatus,
  isMeaningfulDraft,
} from '../../lib/season-goals';
import { ConfirmDialog } from '../confirm-dialog';

type Filter = 'all' | GoalStatus;
type SortKey = 'revenue' | 'target' | 'pct' | 'weeks';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'at-risk', label: 'At Risk' },
  { value: 'on-track', label: 'On Track' },
  { value: 'ahead', label: 'Ahead' },
  { value: 'no-goal', label: 'No goal set' },
];

const STATUS_COLOR: Record<GoalStatus, string> = {
  'no-goal': 'text-muted-foreground',
  'at-risk': 'text-red-600',
  'on-track': 'text-amber-600',
  ahead: 'text-green-600',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

interface SeasonProgressGoalsProps {
  orgPid: string;
  year: number;
  /** Open with the goals ready to edit, as a "Set goal" link does. */
  startEditing?: boolean;
  /** Called when the edit mode ends, so the page can drop `edit` from its address. */
  onStopEditing?: () => void;
}

/**
 * Goals (Season Progress until 22 Oct): each boat's revenue and booked weeks against its goal, and
 * the goals themselves, edited in the same table (#205). "Copy" fills the
 * year's missing goals with the year before's targets.
 */
export function SeasonProgressGoals({
  orgPid,
  year,
  startEditing = false,
  onStopEditing,
}: SeasonProgressGoalsProps) {
  const [filter, setFilter] = useState<Filter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('revenue');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(startEditing);
  const stopEditing = () => {
    setEditing(false);
    onStopEditing?.();
  };

  const { data: boats = [], isLoading: boatsLoading } = useQuery({
    queryKey: queryKeys.analytics.revenueByBoat(orgPid, String(year)),
    queryFn: () => analyticsApi.revenueByBoat({ year: String(year) }),
    enabled: !!orgPid,
  });
  const { data: goals = [], isLoading: goalsLoading } = useRevenueGoals(orgPid, year);
  const { data: lastYearGoals = [] } = useRevenueGoals(orgPid, year - 1);

  const qc = useQueryClient();
  const upsert = useUpsertRevenueGoal();
  const remove = useDeleteRevenueGoal(orgPid, year);
  const copy = useCopyRevenueGoals(orgPid, year);

  const [drafts, setDrafts] = useState<Record<string, GoalDraft>>({});
  const [pendingDelete, setPendingDelete] = useState<GoalDraft | null>(null);

  // Rebuild the edit rows when boats or goals refetch, keeping rows mid-edit.
  useEffect(() => {
    if (boatsLoading || goalsLoading) return;
    setDrafts((prev) => buildGoalDrafts(boats, goals, prev));
  }, [boats, goals, boatsLoading, goalsLoading]);

  const dirtyDrafts = useMemo(() => Object.values(drafts).filter((d) => d.dirty), [drafts]);

  const updateDraft = (boatPid: string, patch: Partial<GoalDraft>) => {
    setDrafts((prev) => {
      const existing = prev[boatPid];
      if (!existing) return prev;
      return { ...prev, [boatPid]: { ...existing, ...patch, dirty: true } };
    });
  };

  const cancelEditing = () => {
    setDrafts(buildGoalDrafts(boats, goals, {}));
    stopEditing();
  };

  const saveAll = async () => {
    if (dirtyDrafts.length === 0) {
      stopEditing();
      return;
    }

    const meaningful = dirtyDrafts.filter(isMeaningfulDraft);
    const skipped = dirtyDrafts.length - meaningful.length;
    if (meaningful.length === 0) {
      setDrafts(buildGoalDrafts(boats, goals, {}));
      stopEditing();
      toast.info('Nothing to save');
      return;
    }
    const results = await Promise.allSettled(
      meaningful.map((d) =>
        upsert.mutateAsync({
          boatPid: d.boatPid,
          data: {
            year,
            targetRevenue: Number(d.targetRevenue) || 0,
            targetWeeks: Number(d.targetWeeks) || 0,
            currency: d.currency,
            notes: d.notes || undefined,
          },
        })
      )
    );

    const succeeded = new Set<string>();
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') succeeded.add(meaningful[i]!.boatPid);
    });

    // Saved and skipped rows are done; failed rows stay dirty for a retry.
    setDrafts((prev) => {
      const next = { ...prev };
      for (const d of dirtyDrafts) {
        const row = next[d.boatPid];
        if (!row) continue;
        if (succeeded.has(d.boatPid)) next[d.boatPid] = { ...row, dirty: false, hasGoal: true };
        else if (!meaningful.includes(d)) next[d.boatPid] = { ...row, dirty: false };
      }
      return next;
    });

    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.revenueGoals.list(orgPid, year) }),
      qc.invalidateQueries({ queryKey: queryKeys.analytics.revenueByBoat(orgPid, String(year)) }),
    ]);

    const failed = results.length - succeeded.size;
    if (failed === 0) {
      const note = skipped > 0 ? ` (${plural(skipped, 'empty row')} skipped)` : '';
      toast.success(`Saved ${plural(succeeded.size, 'goal')}.${note}`);
      stopEditing();
    } else {
      toast.error(`Saved ${succeeded.size} of ${results.length}; ${failed} failed.`);
    }
  };

  const toCopy = boatsToCopyGoals(
    boats.map((b) => b.boatPid),
    goals,
    lastYearGoals
  );
  const copyGoals = () =>
    copy.mutate(undefined, {
      onSuccess: ({ copied, kept }) => {
        const keptNote =
          kept > 0 ? ` ${plural(kept, 'boat')} already had a ${year} goal and kept it.` : '';
        toast.success(`Copied ${plural(copied, 'goal')} from ${year - 1}.${keptNote}`);
      },
      onError: () => toast.error(`Could not copy the ${year - 1} goals`),
    });

  const filtered = useMemo(() => {
    const lower = search.toLowerCase();
    return boats
      .filter((b) => (search ? b.boatName.toLowerCase().includes(lower) : true))
      .filter((b) => filter === 'all' || goalStatus(b) === filter);
  }, [boats, filter, search]);

  const sorted = useMemo(() => {
    const cmp = (a: BoatRevenueBreakdown, b: BoatRevenueBreakdown) => {
      switch (sortKey) {
        case 'revenue':
          return b.totalRevenue - a.totalRevenue;
        case 'target':
          return (b.targetRevenue ?? 0) - (a.targetRevenue ?? 0);
        case 'pct': {
          const ap = a.targetRevenue ? a.totalRevenue / a.targetRevenue : 0;
          const bp = b.targetRevenue ? b.totalRevenue / b.targetRevenue : 0;
          return bp - ap;
        }
        case 'weeks':
          return b.bookedWeeks - a.bookedWeeks;
      }
    };
    return filtered.slice().sort(cmp);
  }, [filtered, sortKey]);

  const totals = useMemo(() => {
    const totalRevenue = boats.reduce((s, b) => s + b.totalRevenue, 0);
    const totalTarget = boats.reduce((s, b) => s + (b.targetRevenue ?? 0), 0);
    // Sum exact days and convert once. Summing per-boat weeks the API has
    // already rounded drifts from the true total (#206).
    const totalWeeks = bookedWeeksFromDays(boats.reduce((s, b) => s + b.bookedDays, 0));
    const totalTargetWeeks = boats.reduce((s, b) => s + (b.targetWeeks ?? 0), 0);
    return {
      totalRevenue,
      totalTarget,
      pct: totalTarget > 0 ? Math.round((totalRevenue / totalTarget) * 100) : 0,
      totalWeeks,
      totalTargetWeeks,
    };
  }, [boats]);

  if (boatsLoading || goalsLoading) return <Skeleton className="h-[600px] w-full" />;

  if (boats.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">
            Connect your fleet to start tracking revenue goals.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          {/* Single-currency v1; see #89 for multi-currency formatting. */}
          <p className="text-sm text-muted-foreground">
            €{Math.round(totals.totalRevenue / 1000)}k earned · €
            {Math.round(totals.totalTarget / 1000)}k target · {totals.pct}% achieved ·{' '}
            {totals.totalWeeks.toFixed(0)} of {totals.totalTargetWeeks} weeks
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={`rounded-full border px-3 py-1 text-xs ${
                  filter === f.value ? 'bg-primary text-primary-foreground' : 'bg-background'
                }`}
              >
                {f.label}
              </button>
            ))}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Input
                placeholder="Search boats..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-48"
              />
              <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Sort by Revenue</SelectItem>
                  <SelectItem value="target">Sort by Target</SelectItem>
                  <SelectItem value="pct">Sort by % achieved</SelectItem>
                  <SelectItem value="weeks">Sort by Weeks</SelectItem>
                </SelectContent>
              </Select>
              {editing ? (
                <>
                  <Button variant="ghost" onClick={cancelEditing}>
                    Cancel
                  </Button>
                  <Button onClick={saveAll} disabled={upsert.isPending}>
                    {upsert.isPending
                      ? 'Saving...'
                      : dirtyDrafts.length > 0
                        ? `Save (${dirtyDrafts.length})`
                        : 'Done'}
                  </Button>
                </>
              ) : (
                <>
                  {toCopy.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={copyGoals}
                      disabled={copy.isPending}
                      title={`Give the boats without a ${year} goal their ${year - 1} targets. Goals already set stay as they are.`}
                    >
                      <Copy className="size-4" />
                      Copy {year - 1} goals ({toCopy.length})
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setEditing(true)}>
                    <Pencil className="size-4" />
                    Edit goals
                  </Button>
                </>
              )}
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Boat</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead className="text-right">%</TableHead>
                <TableHead className="text-right">Weeks</TableHead>
                {editing && <TableHead>Notes</TableHead>}
                {editing && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((b) => {
                const pct = b.targetRevenue ? (b.totalRevenue / b.targetRevenue) * 100 : null;
                const draft = editing ? drafts[b.boatPid] : undefined;
                return (
                  <TableRow key={b.boatPid}>
                    <TableCell>
                      <div className="font-medium">{b.boatName}</div>
                      {b.model && <div className="text-xs text-muted-foreground">{b.model}</div>}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      €{Math.round(b.totalRevenue).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {draft ? (
                        <div className="flex items-center justify-end gap-1">
                          <Label className="text-xs text-muted-foreground">{draft.currency}</Label>
                          <Input
                            type="number"
                            min={0}
                            value={draft.targetRevenue}
                            onChange={(e) =>
                              updateDraft(b.boatPid, { targetRevenue: e.target.value })
                            }
                            placeholder="0"
                            aria-label={`${b.boatName} target revenue`}
                            className="w-32 text-right"
                          />
                        </div>
                      ) : b.targetRevenue ? (
                        `€${Math.round(b.targetRevenue).toLocaleString()}`
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditing(true)}
                          className="text-primary hover:underline"
                        >
                          Set goal
                        </button>
                      )}
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono tabular-nums ${STATUS_COLOR[goalStatus(b)]}`}
                    >
                      {pct === null ? '-' : `${pct.toFixed(0)}%`}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {draft ? (
                        <div className="flex items-center justify-end gap-1">
                          {roundBookedWeeks(b.bookedWeeks)} /
                          <Input
                            type="number"
                            min={0}
                            max={52}
                            value={draft.targetWeeks}
                            onChange={(e) =>
                              updateDraft(b.boatPid, { targetWeeks: e.target.value })
                            }
                            placeholder="0"
                            aria-label={`${b.boatName} target weeks`}
                            className="w-20 text-right"
                          />
                        </div>
                      ) : (
                        <>
                          {roundBookedWeeks(b.bookedWeeks)}
                          {b.targetWeeks !== null && (
                            <span className="text-muted-foreground"> / {b.targetWeeks}</span>
                          )}
                        </>
                      )}
                    </TableCell>
                    {editing && (
                      <TableCell>
                        <Input
                          value={draft?.notes ?? ''}
                          onChange={(e) => updateDraft(b.boatPid, { notes: e.target.value })}
                          placeholder="optional"
                          aria-label={`${b.boatName} notes`}
                        />
                      </TableCell>
                    )}
                    {editing && (
                      <TableCell>
                        {draft?.hasGoal && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setPendingDelete(draft)}
                            aria-label={`Delete ${b.boatName} goal`}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <p className="text-xs text-muted-foreground">
            Showing {sorted.length} of {boats.length} boats
          </p>
        </CardContent>
      </Card>

      {pendingDelete && (
        <ConfirmDialog
          open={true}
          onOpenChange={(open) => !open && setPendingDelete(null)}
          title="Delete revenue goal?"
          description={`This will remove the ${year} target for ${pendingDelete.boatName}.`}
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={async () => {
            await remove.mutateAsync(pendingDelete.boatPid);
            toast.success(`Deleted goal for ${pendingDelete.boatName}`);
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
}
